/**
 * Compose Email Drawer.
 *
 * A reusable slide-over drawer for composing and sending emails.
 * Supports pre-filled recipients, subjects, and body content from
 * business documents. Can also select from saved email templates.
 */

import { useEffect, useState } from "react";
import { Mail, Send } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import ActionDrawer from "@/components/ui/ActionDrawer";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

interface ComposeEmailProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-fill the To field. */
  defaultTo?: string;
  /** Pre-fill the To name. */
  defaultToName?: string;
  /** Pre-fill the subject line. */
  defaultSubject?: string;
  /** Pre-fill the body (HTML). */
  defaultBody?: string;
  /** Link to a business document. */
  entityType?: string;
  /** Document ID. */
  entityId?: string;
  /**
   * Send through the document route, which renders the document server-side and
   * attaches it as a PDF. Requires `entityType` and `entityId`; the body becomes
   * a covering note rather than the whole message.
   */
  attachDocument?: boolean;
}

function ComposeEmail({
  isOpen,
  onClose,
  defaultTo = "",
  defaultToName = "",
  defaultSubject = "",
  defaultBody = "",
  entityType,
  entityId,
  attachDocument = false,
}: ComposeEmailProps) {
  const [to, setTo] = useState(defaultTo);
  const [toName, setToName] = useState(defaultToName);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  /**
   * Seed the form each time the drawer opens.
   *
   * This used to call four setState functions *during render*, guarded on
   * `defaultTo` being truthy. It was dormant only because no caller passed a
   * `defaultTo`; the moment one did — which the sales order page now does, so the
   * invoice email is not addressed to nobody — it would re-fire on every render
   * where `to !== defaultTo`, wiping out whatever the user had typed into the To
   * field as they typed it.
   *
   * An effect keyed on `isOpen` seeds once per opening and then leaves the form
   * alone, which is what the original comment said it wanted.
   */
  useEffect(() => {
    if (!isOpen) return;
    setTo(defaultTo);
    setToName(defaultToName);
    setSubject(defaultSubject);
    setBody(defaultBody);
    // Deliberately keyed on `isOpen` alone: re-seeding because a default changed
    // while the drawer is open would be the same bug in a different shape.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (attachDocument && entityType && entityId) {
        return (
          await apiClient.post("/api/v1/email/send-document", {
            entity_type: entityType,
            entity_id: entityId,
            to_email: to,
            to_name: toName || undefined,
            subject,
            extra_message: body || undefined,
          })
        ).data;
      }
      return (
        await apiClient.post("/api/v1/email/send", {
          to_email: to,
          to_name: toName || undefined,
          subject,
          body_html: body || "<p>No content</p>",
          entity_type: entityType || undefined,
          entity_id: entityId || undefined,
        })
      ).data;
    },
    onSuccess: (data) => {
      if (data.status === "sent") {
        toast.success("Email sent", `Delivered to ${to}`);
      } else if (data.status === "queued") {
        toast.info(
          "Email queued",
          "SMTP is not configured. Email saved for later delivery.",
        );
      } else {
        toast.error("Email failed", data.error || "Unknown error");
      }
      onClose();
    },
    /**
     * Surface the server's reason. The document route can fail in ways the plain
     * send cannot — a 503 when the PDF renderer's system libraries are missing,
     * a 404 when the order does not exist — and "Failed to send email" tells
     * nobody which. The drawer deliberately stays open on an error so the user
     * does not lose what they typed.
     */
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      toast.error(
        "Failed to send email",
        typeof detail === "string" ? detail : undefined,
      );
    },
  });

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Compose Email"
      description={
        attachDocument
          ? "The document is attached as a PDF."
          : "Send an email to a customer, vendor, or contact."
      }
      icon={Mail}
      size="md"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMutation.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="To (Email)"
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            required
            placeholder="recipient@example.com"
          />
          <Input
            label="Name"
            value={toName}
            onChange={(e) => setToName(e.target.value)}
            placeholder="Recipient name (optional)"
          />
        </div>
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          placeholder="Email subject line"
        />
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={
              attachDocument
                ? "Add a note to go with the document (optional)..."
                : "Write your message here..."
            }
            className="mt-1.5 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
          />
        </div>
        {entityType && (
          <p className="text-xs text-[var(--text-tertiary)]">
            {attachDocument
              ? `The ${entityType.replace("_", " ")} is generated fresh and attached as a PDF when you send.`
              : `This email will be linked to ${entityType.replace("_", " ")}${entityId ? ` (${entityId.slice(0, 8)}...)` : ""} in the email history.`}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" isLoading={sendMutation.isPending}>
            <Send className="h-3.5 w-3.5" /> Send Email
          </Button>
        </div>
      </form>
    </ActionDrawer>
  );
}

export default ComposeEmail;
